import type React from 'react';

export type Step = 1 | 2 | 3 | 4;

export type ComplianceDocumentType =
  | 'DOC'
  | 'DOI'
  | 'DOP'
  | 'USER_MANUAL'
  | 'REPAIR_MANUAL'
  | 'EPREL_FICHE'
  | 'CARBON_FOOTPRINT_REPORT'
  | 'SAFETY_MANUAL'
  | 'DISASSEMBLY_GUIDE'
  | 'MAINTENANCE_GUIDE'
  | 'DEMOLITION_GUIDE'
  | 'VOC_CERTIFICATE'
  | 'EUDR_DDS_STATEMENT'
  | 'CARE_INSTRUCTIONS'
  | 'FIBER_TEST_REPORT'
  | 'MILL_TEST_CERTIFICATE'
  | 'CBAM_REPORT'
  | 'TYPE_APPROVAL'
  | 'TIRE_LABEL_FICHE'
  | 'SAFETY_DATA_SHEET'
  | 'UFI_DOSSIER'
  | 'TECHNICAL_DATA_SHEET';

export type CategoryCode =
  | 'ELECTRONICS_ICT'
  | 'BATTERIES_STORAGE'
  | 'TEXTILES_APPAREL'
  | 'INDUSTRIAL_MACHINERY'
  | 'CONSTRUCTION_PRODUCTS'
  | 'FURNITURE_MATTRESSES'
  | 'IRON_STEEL_METALS'
  | 'TIRES_AUTOMOTIVE'
  | 'CHEMICALS'
  | 'OTHER_HARDWARE';

export const productCategoryOptions: Array<{
  code: CategoryCode;
  name: string;
}> = [
  { code: 'ELECTRONICS_ICT', name: 'Electronics & ICT Equipment' },
  { code: 'BATTERIES_STORAGE', name: 'Batteries & Energy Storage' },
  { code: 'TEXTILES_APPAREL', name: 'Textiles & Apparel' },
  { code: 'INDUSTRIAL_MACHINERY', name: 'Industrial Machinery' },
  { code: 'CONSTRUCTION_PRODUCTS', name: 'Construction Products' },
  { code: 'FURNITURE_MATTRESSES', name: 'Furniture & Mattresses' },
  { code: 'IRON_STEEL_METALS', name: 'Iron, Steel & Base Metals' },
  { code: 'TIRES_AUTOMOTIVE', name: 'Tires & Vehicle Components' },
  { code: 'CHEMICALS', name: 'Chemicals & Materials' },
  { code: 'OTHER_HARDWARE', name: 'Other / General Hardware' },
];

export type Material = {
  material_name: string;
  weight_percentage: string;
  recycled_content_percentage: string;
  is_recyclable: boolean;
};

export type SupplyChainTraceabilityRow = {
  tier: 'tier_1' | 'tier_2' | 'tier_3';
  company_name: string;
  country_code: string;
  facility_id_or_address: string;
  process_component: string;
};

export type SvhcDeclarationRow = {
  substance_name: string;
  cas_ec_number: string;
  present_in_component: string;
  scip_dossier_number: string;
};

export type PackagingItem = {
  packaging_category: 'primary' | 'secondary' | 'tertiary' | 'ecommerce' | '';
  material_type: 'pap20' | 'pap21' | 'ldpe04' | 'pet01' | 'wood' | 'other' | '';
  weight_grams: string;
  pcr_content_percent: string;
  recyclability_status:
    | 'recyclable_100'
    | 'requires_separation'
    | 'non_recyclable'
    | '';
};

export type MachineClassification =
  | 'STANDARD_MACHINERY'
  | 'HIGH_RISK_MACHINERY'
  | 'PARTLY_COMPLETED'
  | 'PARTLY_COMPLETED_MACHINERY';

export type CeResponsibleEntityType =
  | 'EU_MANUFACTURER'
  | 'EU_IMPORTER'
  | 'AUTHORIZED_REPRESENTATIVE';

export type ProductForm = {
  name: string;
  productSummary: string;
  productWeight: string;
  productWeightUnit: 'g' | 'kg';
  skuModel: string;
  gtinEan: string;
  brandName: string;
  categoryCode: CategoryCode | '';
  manufacturerName: string;
  manufacturerAddress: string;
  importedOutsideEu: boolean;
  importerName: string;
  importerEmail: string;
  importerAddress: string;
  importerEori: string;
  seasonCollection: string;
  targetAudience: string[];
  hardwareRevision: string;
  machineClassification: MachineClassification | '';
  ceResponsiblePerson: string;
  declarationOfConformityFile: File | null;
  declarationOfIncorporationFile: File | null;
  notifiedBodyNumber: string;
  notifiedBodyCertNumber: string;
  notifiedBodyCertificateFile: File | null;
  ceResponsibleEntityType: CeResponsibleEntityType | '';
  ceResponsibleCompanyName: string;
  ceResponsibleEuAddress: string;
  ceResponsibleContactEmail: string;
  materials: Material[];
  svhcStatus: 'none' | 'contains' | '';
  svhcDeclarations: SvhcDeclarationRow[];
  containsElectricalComponents: boolean;
  rohsStatus: 'without_exemption' | 'with_exemption' | 'not_applicable' | '';
  rohsExemptionCode: string;
  customExemptionDetails: string;
  rohsExclusionReason: string;
  rohsSupportingDocumentFile: File | null;
  dopDocumentFile: File | null;
  vocEmissionClass: 'A_PLUS' | 'A' | 'B' | 'C' | 'UNTESTED' | '';
  eudrDdsReference: string;
  flameRetardantsPresent: boolean;
  flameRetardantChemicalNames: string[];
  ufiCode: string;
  clpHazardPhrases: string[];
  criticalRawMaterials: boolean;
  criticalRawMaterialsList: string[];
  weeeRegistrationNumber: string;
  includeSupplyChainTraceability: boolean;
  supplyChainTraceability: SupplyChainTraceabilityRow[];
  popsPfasStatus:
    | 'not_verified'
    | 'verified_compliant'
    | 'contains_restricted'
    | '';
  popsPfasTestReportFile: File | null;
  popsPfasExemptionDetails: string;
  includeConsumerPackaging: boolean;
  packagingItems: PackagingItem[];
  packagingEprRegistration: string;
  documentIds: string[];
  complianceDocumentFiles: Partial<Record<ComplianceDocumentType, File | null>>;
  carbonFootprint: string;
  lifespan: string;
  lifespanUnit: 'years' | 'months';
  repairabilityScore: string;
  repairManualFile: File | null;
  disassemblyGuideFile: File | null;
  demolitionGuideFile: File | null;
  demolitionGuideUrl: string;
  recyclingInstructions: string;
  ratedCapacityAh: string;
  ratedEnergyKwh: string;
  nominalVoltageV: string;
  minimumVoltageV: string;
  maximumVoltageV: string;
  powerCapabilityLimits: string;
  roundTripEfficiencyPercent: string;
  cycleLifetimeDescriptor: string;
  operatingTemperatureWindow: string;
  extinguishingAgents: string;
  safetyHandlingMeasures: string;
  restrictTechnicalSpecs: boolean;
  careSymbols: string[];
  colorFastnessRating: string;
  pillingResistanceRating: string;
  waterConsumption: string;
  hasRemovableCovers: boolean;
  productionRoute:
    | 'EAF_ELECTRIC_ARC'
    | 'BF_BOF_BLAST_FURNACE'
    | 'RECYCLED_SECONDARY'
    | '';
  cbamEmbeddedEmissions: string;
  rollingResistanceClass: 'A' | 'B' | 'C' | 'D' | 'E' | '';
  wetGripClass: 'A' | 'B' | 'C' | 'D' | 'E' | '';
  abrasionRateMgKm: string;
  isRetreadable: boolean;
  surfactantBiodegradabilityPercentage: string;
  sparePartsAvailability: string;
  sparePartsAvailabilityYears: string;
  sparePartsCatalogUrl: string;
  takeBackScheme: string;
  microplasticsStatus: 'not_applicable' | 'untested' | 'tested' | '';
  microplasticsReportFile: File | null;
  eprRegistrationNumber: string;
  softwareSupportYears: string;
  softwareSupportExpiry: string;
  energyEfficiencyRating: string;
};

export type UpdateProductForm = <Key extends keyof ProductForm>(
  key: Key,
  value: ProductForm[Key],
  options?: { markDirty?: boolean },
) => void;

export type UpdateMaterial = <Key extends keyof Material>(
  index: number,
  key: Key,
  value: Material[Key],
) => void;

export type UpdateSupplyChainSupplier = <
  Key extends keyof SupplyChainTraceabilityRow,
>(
  index: number,
  key: Key,
  value: SupplyChainTraceabilityRow[Key],
) => void;

export type UpdateSvhcDeclaration = <Key extends keyof SvhcDeclarationRow>(
  index: number,
  key: Key,
  value: SvhcDeclarationRow[Key],
) => void;

export type UpdatePackagingItem = <Key extends keyof PackagingItem>(
  index: number,
  key: Key,
  value: PackagingItem[Key],
) => void;

export type ProductMetadata = {
  product_weight_value?: number | null;
  product_weight_unit?: ProductForm['productWeightUnit'] | null;
  manufacturer_name?: string | null;
  manufacturer_address?: string | null;
  imported_outside_eu?: boolean;
  importer_name?: string | null;
  importer_email?: string | null;
  importer_address?: string | null;
  importer_eori?: string | null;
  season_collection?: string | null;
  target_audience?: string[] | null;
  hardware_revision?: string | null;
  machine_classification?: string | null;
  ce_responsible_person?: string | null;
  declaration_of_conformity_document_id?: string | null;
  declaration_of_incorporation_document_id?: string | null;
  notified_body_number?: string | null;
  notified_body_cert_number?: string | null;
  notified_body_certificate_document_id?: string | null;
  ce_responsible_person_entity_type?: CeResponsibleEntityType | null;
  ce_responsible_person_company_name?: string | null;
  ce_responsible_person_eu_address?: string | null;
  ce_responsible_person_contact_email?: string | null;
  contains_electrical_components?: boolean | null;
  rohs_exemption_code?: string | null;
  custom_exemption_details?: string | null;
  rohs_exclusion_reason?: string | null;
  dop_document_path?: string | null;
  voc_emission_class?: ProductForm['vocEmissionClass'] | null;
  eudr_dds_reference?: string | null;
  flame_retardants_present?: boolean | null;
  flame_retardant_chemical_names?: string[] | null;
  ufi_code?: string | null;
  clp_hazard_phrases?: string[] | null;
  critical_raw_materials?: boolean;
  critical_raw_materials_list?: string[] | null;
  weee_registration_number?: string | null;
  include_supply_chain_traceability?: boolean;
  supply_chain_traceability?: SupplyChainTraceabilityRow[] | null;
  include_consumer_packaging?: boolean;
  lifespan_unit?: ProductForm['lifespanUnit'];
  demolition_guide_url?: string | null;
  has_removable_covers?: boolean | null;
  production_route?: ProductForm['productionRoute'] | null;
  cbam_embedded_emissions?: number | null;
  rolling_resistance_class?: ProductForm['rollingResistanceClass'] | null;
  wet_grip_class?: ProductForm['wetGripClass'] | null;
  abrasion_rate_mg_km?: number | null;
  is_retreadable?: boolean | null;
  surfactant_biodegradability_percentage?: number | null;
  software_support_expiry?: string | null;
  energy_efficiency_rating?: string | null;
  compliance_document_paths?: Partial<
    Record<ComplianceDocumentType, string>
  > | null;
};

export type ExistingProductRecord = {
  id: string;
  name: string;
  description?: string | null;
  sku_model: string;
  gtin_ean: string | null;
  brand_name: string;
  image_url: string | null;
  metadata?: ProductMetadata | null;
  product_categories?:
    | { code?: string | null }
    | { code?: string | null }[]
    | null;
};

export type ExistingDeclarationRecord = {
  declaration_type: string;
  status: string;
  svhc_present: boolean | null;
  substance_name: string | null;
  cas_ec_number: string | null;
  present_in_component: string | null;
  scip_number: string | null;
  document_id: string | null;
  documents?:
    | {
        title: string | null;
        storage_path: string | null;
      }
    | {
        title: string | null;
        storage_path: string | null;
      }[]
    | null;
};

export type ExistingCircularityRecord = {
  carbon_footprint_kg_co2e: number | null;
  expected_lifetime_years: number | null;
  repairability_score: number | null;
  repair_manual_url: string | null;
  disassembly_guide_url: string | null;
  recycling_instructions: string | null;
  rated_capacity_ah: number | null;
  rated_energy_kwh: number | null;
  nominal_voltage_v: number | null;
  minimum_voltage_v: number | null;
  maximum_voltage_v: number | null;
  power_capability_limits: string | null;
  round_trip_efficiency_percent: number | null;
  cycle_lifetime_descriptor: string | null;
  operating_temperature_window: string | null;
  extinguishing_agents: string | null;
  safety_handling_measures: string | null;
  water_consumption_liters: number | null;
  color_fastness_rating: number | null;
  pilling_resistance_rating: number | null;
  spare_parts_availability: string | null;
  spare_parts_availability_years: number | null;
  spare_parts_catalog_url: string | null;
  take_back_scheme: string | null;
  microplastics_status: string | null;
  epr_registration_number: string | null;
  care_symbols: string[] | null;
  software_support_years: number | null;
};

export type ExistingPassportRecord = {
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'WITHDRAWN' | null;
  published_at?: string | null;
  visibility_settings?: {
    technical_specs_access?: 'restricted' | 'public';
  } | null;
};

export type EchaCandidateSubstance = {
  substance_name: string;
  ec_number: string | null;
  cas_number: string | null;
};

export type CareSymbolOption = {
  value: string;
  label: string;
  icon: React.ReactNode;
};

export type CareSymbolCategory = {
  category: string;
  options: CareSymbolOption[];
};
